import { Reservation } from "types/reservations";
import { db } from "utils/firebase";

export type GuestReservation = Pick<
    Reservation,
    | "id"
    | "restaurant"
    | "date"
    | "time"
    | "guests"
    | "tables"
    | "status"
    | "started"
    | "done"
    | "customId"
>;

export const reservationDay = (value: string): string | null => {
    const day = value?.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
    const parsed = new Date(day + "T12:00:00Z");
    return !isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day
        ? day
        : null;
};

export const loadGuestReservations = async (
    restaurantId: string,
    selectedGuestId: string
): Promise<GuestReservation[]> => {
    if (!restaurantId || !selectedGuestId) return [];
    const suffix = `-${restaurantId}`;
    const guestId = selectedGuestId.endsWith(suffix)
        ? selectedGuestId.slice(0, -suffix.length)
        : selectedGuestId;
    if (!guestId) return [];
    const snapshots = await Promise.all(
        [guestId, `${guestId}${suffix}`].map((id) =>
            db
                .collection("requestsV03")
                .where("restaurant", "==", restaurantId)
                .where("guest.id", "==", id)
                .get()
        )
    );
    const reservations = new Map<string, GuestReservation>();
    snapshots.forEach((snapshot) =>
        snapshot.forEach((doc) => {
            const data = doc.data() as Reservation;
            if (data.restaurant !== restaurantId || !reservationDay(data.date))
                return;
            reservations.set(doc.id, {
                id: doc.id,
                restaurant: data.restaurant,
                date: data.date,
                time: data.time,
                guests: data.guests,
                tables: data.tables,
                status: data.status,
                started: data.started,
                done: data.done,
                customId: data.customId
            });
        })
    );
    return [...reservations.values()];
};

export const groupGuestReservations = (
    reservations: GuestReservation[],
    today: string
) => {
    const past = (reservation: GuestReservation) =>
        !!reservation.done || (reservationDay(reservation.date) || "") < today;
    const order = (reservation: GuestReservation) =>
        `${reservationDay(reservation.date)}T${reservation.time || "00:00"}`;
    return [
        {
            title: "Upcoming guest reservations",
            reservations: reservations
                .filter((r) => !past(r))
                .sort((a, b) => order(a).localeCompare(order(b)))
        },
        {
            title: "Past guest reservations",
            reservations: reservations
                .filter(past)
                .sort((a, b) => order(b).localeCompare(order(a)))
        }
    ];
};

export const guestReservationPath = (
    restaurantId: string,
    reservation: GuestReservation,
    pathname: string
) => {
    const day = reservationDay(reservation.date);
    if (!day || reservation.restaurant !== restaurantId) return null;
    const view =
        pathname.match(
            /^\/[^/]+\/reservations\/[^/]+\/(tablePlan|list|reservationList)(?:\/|$)/
        )?.[1] || "tablePlan";
    return `/${encodeURIComponent(
        restaurantId
    )}/reservations/${day}/${view}?reservationId=${encodeURIComponent(
        reservation.id
    )}`;
};
